import { useEffect, useState } from 'react';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Catalogue } from '../../types/catalogue.type';
import { Appliance } from '../../types/appliance.type';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { useNavigate } from 'react-router-dom';
import {
  getStorageItem,
  setStorageItem,
} from '../../services/storage/session-storage/sessionStorage.service';

import cookerIcon from '../../assets/icons/appliances/cooker.svg';
import dishwasherIcon from '../../assets/icons/appliances/dishwasher.svg';
import laundryIcon from '../../assets/icons/appliances/laundry.svg';
import miscellaneousIcon from '../../assets/icons/appliances/misc.svg';
import refrigeratorIcon from '../../assets/icons/appliances/refrigerator.svg';
import smallApplianceIcon from '../../assets/icons/appliances/smallAppliance.svg';

const iconPallet: Record<string, string> = {
  cook: cookerIcon,
  dish: dishwasherIcon,
  laun: laundryIcon,
  misc: miscellaneousIcon,
  rfrg: refrigeratorIcon,
  sdas: smallApplianceIcon,
};

export function ApplianceTypePage() {
  const { VITE_BRAND_CODE } = import.meta.env;
  const [catalogue, setCatalogue] = useState<Catalogue[] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [appliances, setAppliances] = useState<Appliance[] | null>(null);
  const [selectedAppliance, setSelectedApplianceCode] = useState<string | null>(null);

  const selectedStyles =
    'bg-(--button-bg-outline-active-color) text-(--button-text-outline-active-color)';
  const unselectedStyles =
    'bg-(--button-border-outline-default-color) text-(--button-text-outline-color)';
  const applianceTileButtonStyles =
    'rounded-(--button-border-radius) hover:bg-(--button-bg-outline-hover-color) hover:text-(--button-text-outline-hover-color) hover:(--button-border-outline-hover-color) disabled:bg-(--button-bg-outline-disabled-color) disabled:text-(--button-text-outline-disabled-color) focus:border-(--button-border-outline-focus-color) focus:ring-2 focus:ring-(--button-border-outline-color) focus:outline-(--button-border-outline-outline-focus-color) focus:inset-ring-(--button-border-outline-focus-color) active:bg-(--button-bg-outline-active-color) active:text-(--button-text-outline-active-color) active:border-(--button-border-outline-active-color)';
  const applianceTileStyles =
    'flex flex-col justify-center items-center text-center text-sm border-1  py-3 cursor-pointer border-(--button-border-outline-focus-color)';

  const selectedCategoryStyle = (brandCatalogue: Catalogue) =>
    selectedCategory === brandCatalogue.categoryCode ? selectedStyles : unselectedStyles;

  const selectedApplianceType = (appliance: Appliance) =>
    selectedAppliance === appliance.applianceCode
      ? selectedStyles + ' ' + 'px-2'
      : unselectedStyles + ' ' + ' px-3';

  useEffect(() => {
    const storedAppliance = getStorageItem<string>('appliance');
    const storedCategory = getStorageItem<string>('category');

    if (storedCategory) {
      setSelectedCategory(storedCategory);
    }
    if (storedAppliance) {
      setSelectedApplianceCode(storedAppliance);
    }
  }, []);

  useEffect(() => {
    const catalogueFromStorage = getStorageItem<Catalogue[]>('catalogue');
    if (catalogueFromStorage) {
      setCatalogue(catalogueFromStorage);
    }
  }, [VITE_BRAND_CODE]);

  useEffect(() => {
    if (catalogue && catalogue.length >= 0 && selectedCategory) {
      const appliances = catalogue.find(
        (category) => category.categoryCode === selectedCategory,
      )?.appliances;
      setAppliances(appliances ?? null);
    }
  }, [catalogue, selectedCategory]);

  const navigate = useNavigate();

  const routeChange = () => {
    const path = `/model-number`;
    if (selectedAppliance) {
      setStorageItem('category', selectedCategory);
      setStorageItem('appliance', selectedAppliance);
    }
    navigate(path);
  };

  return (
    <>
      <div className="flex justify-center items-center my-8">
        <div className="w-2xl h-auto p-5 flex flex-col justify-center rounded-(--border-radius) bg-neutral-0">
          <ProgressBars total={6} current={1} />
          <h1 className="mb-4 text-2xl">What type of appliance are you registering?</h1>
          <p className="mb-4">Some Body copy goes here</p>
          <h2 className="mb-4 text-xl">Select an appliance category</h2>

          <div className="flex flex-row flex-wrap gap-5">
            {catalogue ? (
              catalogue.map((brandCatalogue: Catalogue) => (
                <button
                  key={brandCatalogue.categoryCode}
                  aria-pressed={selectedCategory === brandCatalogue.categoryCode}
                  id={`appliance-${brandCatalogue.categoryCode.toLocaleLowerCase()}`}
                  name="appliance-category"
                  value={brandCatalogue.categoryCode}
                  type="button"
                  className={`px-3 ${applianceTileStyles} ${applianceTileButtonStyles} ${selectedCategoryStyle(brandCatalogue)} text-wrap h-[142px] w-[136px] md:h-[122px] md:w-[197px]`}
                  onClick={() => setSelectedCategory(brandCatalogue.categoryCode)}
                >
                  <div className="flex flex-col gap-2 min-w-fit">
                    <img
                      src={iconPallet[brandCatalogue.categoryCode.toLocaleLowerCase()]}
                      alt={`${brandCatalogue.categoryName.toLowerCase()}-icon`}
                      className="max-h-10 self-center"
                    />
                    <p
                      className={`${selectedCategory === brandCatalogue.categoryCode ? 'font-bold' : ''}`}
                    >
                      {brandCatalogue.categoryName}
                    </p>
                  </div>
                </button>
              ))
            ) : (
              <p>Loading...</p>
            )}
          </div>

          {appliances ? (
            <div className="mt-4">
              <h2 className="mb-4 text-xl">Select an appliance type</h2>
              <div className="flex flex-col gap-5 md:flex-row md:flex-wrap md:items-center">
                {appliances.map((appliance: Appliance) => (
                  <button
                    key={appliance.applianceCode}
                    aria-pressed={selectedAppliance === appliance.applianceCode}
                    id={`appliance-${appliance.applianceCode}`}
                    name="appliance-category"
                    value={appliance.applianceCode}
                    type="button"
                    className={`${applianceTileStyles} ${applianceTileButtonStyles} ${selectedApplianceType(appliance)} text-wrap w-auto py-5 md:w-[197px]`}
                    onClick={() => setSelectedApplianceCode(appliance.applianceCode)}
                  >
                    <p
                      className={`${selectedAppliance === appliance.applianceCode ? 'font-bold' : ''}`}
                    >
                      {appliance.applianceName}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-row mt-4 justify-center md:justify-end">
            <Button onClick={routeChange}>Continue</Button>
          </div>
        </div>
      </div>
    </>
  );
}
